// components/Chat/chatHelpers.jsx
// Shared UI helpers for assistant chats (AgentWorkspace + Zeno):
//  - useAutoResize: textarea that grows with its content up to a max height
//  - isLongText: threshold check for "long" messages
//  - CollapsibleContent: collapses tall content behind a "Show more" toggle
//  - LongTextCard: renders a very long message as a compact attachment-style card
import React, { useState, useRef, useEffect } from 'react';
import { FileText, ChevronDown, ChevronUp } from 'lucide-react';

/* ─── Auto-growing textarea ─────────────────────────────── */
export function useAutoResize(value, maxHeight = 200) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
  }, [value, maxHeight]);
  return ref;
}

/* ─── Long-text detection ───────────────────────────────── */
const LONG_TEXT_CHARS = 650;
const LONG_TEXT_LINES = 12;
export function isLongText(text = '') {
  if (!text) return false;
  return text.length > LONG_TEXT_CHARS || text.split('\n').length > LONG_TEXT_LINES;
}

/* ─── Collapse tall content behind "Show more" ──────────── */
export function CollapsibleContent({
  children,
  collapsedHeight = 340,
  fadeColor = '#1a1a1f',
  className = '',
  enabled = true,
}) {
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);
  const innerRef = useRef(null);

  useEffect(() => {
    const el = innerRef.current;
    if (!el || !enabled) { setOverflowing(false); return; }
    setOverflowing(el.scrollHeight > collapsedHeight + 24);
  }, [children, collapsedHeight, enabled]);

  if (!enabled) return <div className={className}>{children}</div>;

  return (
    <div className={className}>
      <div
        ref={innerRef}
        className="relative overflow-hidden transition-[max-height] duration-300 ease-in-out"
        style={{ maxHeight: expanded || !overflowing ? 'none' : collapsedHeight }}
      >
        {children}
        {overflowing && !expanded && (
          <div
            className="pointer-events-none absolute inset-x-0 bottom-0 h-14"
            style={{ background: `linear-gradient(to top, ${fadeColor}, transparent)` }}
          />
        )}
      </div>
      {overflowing && (
        <button
          onClick={() => setExpanded((e) => !e)}
          className="mt-1.5 flex items-center gap-1 text-xs font-medium text-purple-300 hover:text-purple-200 transition-colors"
        >
          {expanded ? (
            <><ChevronUp className="w-3.5 h-3.5" /> Show less</>
          ) : (
            <><ChevronDown className="w-3.5 h-3.5" /> Show more</>
          )}
        </button>
      )}
    </div>
  );
}

/* ─── Long message rendered as an attachment card ───────── */
export function LongTextCard({ text }) {
  const [open, setOpen] = useState(false);
  const lines = text.split('\n').length;
  const chars = text.length;

  return (
    <div className="min-w-0">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-2.5 rounded-xl bg-black/20 border border-white/15 px-3 py-2 text-left hover:bg-black/30 transition-colors"
      >
        <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-white/10">
          <FileText className="h-4 w-4 text-white/80" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium text-white truncate">Long message</p>
          <p className="text-[10px] text-white/60">
            {chars.toLocaleString()} characters · {lines} line{lines !== 1 ? 's' : ''}
          </p>
        </div>
        {open ? (
          <ChevronUp className="h-4 w-4 flex-shrink-0 text-white/70" />
        ) : (
          <ChevronDown className="h-4 w-4 flex-shrink-0 text-white/70" />
        )}
      </button>
      {open && (
        <p className="mt-2 whitespace-pre-wrap break-words text-sm leading-relaxed">{text}</p>
      )}
    </div>
  );
}
